/**
 * Compare surfaces — the start mutation and everything after it (spec §4.3,
 * §12.7, §12.10).
 *
 * One result fires that engine's own mutation; two fire `startComparison`
 * (`submissionFor` decides, from values the dialog has already stripped of
 * locked results). On success:
 *   - the runs list and both engines' job lists are invalidated;
 *   - ONE persistent `toast.loading` opens, naming the part(s);
 *   - the run is registered with `registerComparisonTrackers` — a pair under
 *     its server group, a single result as a one-member group (`solo:<batch>`,
 *     ruling R21), so the toast ends in the same Compare surfaces wording. A
 *     part that failed to start is registered already failed;
 *   - the chained PDF gets its report tracker (`reportBatchId` for a pair);
 *   - the settings are remembered for this project, and the dialog closes.
 * When NO part started, nothing is followed: one error toast, and the dialog
 * stays open.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from 'jotai';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  COMPARE_PART_LABEL_KEYS,
  registerComparisonTrackers,
  type ComparisonTrackerMember,
  type DeviationTracker,
} from '@/atoms/deviation-progress.atom';
import type { LayerData } from '@/atoms/layers.atom';
import { reportTrackingAtom } from '@/atoms/report-progress.atom';
import { execute } from '@/graphql/execute';
import { comparisonToastText } from '@/hooks/useDeviationProgress';
import type { CompareResult } from '@/lib/analyses/compare/availability';
import { defaultCompareName, type CompareFormValues } from '@/lib/analyses/compare/formSchema';
import type { CompareSubmission } from '@/lib/analyses/compare/payloads';
import { saveRemembered, type RememberEdited } from '@/lib/analyses/compare/rememberedSettings';
import { translateKey } from '@/lib/analyses/translateKey';
import { useInvalidateAnalysisRuns } from '@/lib/analyses/useAnalysisRuns';
import { useDialog } from '@/providers/DialogProvider';
import { startComparison } from '@/queries/comparison.query';
import { startDeviationAnalysis } from '@/queries/deviation.query';
import { startSurfaceDifference } from '@/queries/surfaceDifference.query';

export interface CompareSubmitVars {
  submission: CompareSubmission;
  /** The submitted values, results already narrowed to what is sent. */
  values: CompareFormValues;
  /** Which deviation settings the user set (for the remembered settings). */
  edited: RememberEdited;
}

type Started =
  | { mutation: 'startSurfaceDifference' | 'startDeviationAnalysis'; batchId: string }
  | {
      mutation: 'startComparison';
      groupId: string;
      batchIds: Record<CompareResult, string | null>;
      reportBatchId: string | null;
      failed: Partial<Record<CompareResult, string>>;
      /** Parts whose start the server could not confirm (they keep their batch id). */
      uncertain: CompareResult[];
    };

/** `ComparisonStartFailure.part` → the dialog's result. */
const PART_OF: Record<string, CompareResult> = { SURFACE_DIFFERENCE: 'cutFill', DEVIATION: 'deviation' };

async function start(submission: CompareSubmission): Promise<Started> {
  switch (submission.mutation) {
    case 'startSurfaceDifference': {
      const data = await execute(startSurfaceDifference, { input: submission.input });
      return { mutation: submission.mutation, batchId: data.startSurfaceDifference };
    }
    case 'startDeviationAnalysis': {
      const data = await execute(startDeviationAnalysis, { input: submission.input });
      return { mutation: submission.mutation, batchId: data.startDeviationAnalysis };
    }
    case 'startComparison': {
      const { startComparison: r } = await execute(startComparison, { input: submission.input });
      const failed: Partial<Record<CompareResult, string>> = {};
      for (const f of r.failedToStart) if (PART_OF[f.part]) failed[PART_OF[f.part]] = f.message;
      return {
        mutation: 'startComparison',
        groupId: r.groupId,
        batchIds: { cutFill: r.surfaceDifferenceBatchId ?? null, deviation: r.deviationBatchId ?? null },
        reportBatchId: r.reportBatchId ?? null,
        failed,
        uncertain: r.startUncertain.map((p) => PART_OF[p]).filter((p): p is CompareResult => !!p),
      };
    }
  }
}

export function useCompareSubmit({ projectId, layers }: { projectId: string; layers: readonly LayerData[] }) {
  const { t } = useTranslation('project');
  const store = useStore();
  const queryClient = useQueryClient();
  const invalidateAnalysisRuns = useInvalidateAnalysisRuns();
  const { dismissActiveDialog } = useDialog();

  const layerOf = (id: string) => layers.find((l) => l.id === id);
  /** The layers already derived from `id`: the toast resolves on a NEW one. */
  const derivedFrom = (id: string) =>
    layers.filter((l) => (l.metadata as { derivedFrom?: string } | null)?.derivedFrom === id).map((l) => l.id);

  return useMutation({
    mutationFn: ({ submission }: CompareSubmitVars) => start(submission),
    onSuccess: (started, { submission, values, edited }) => {
      invalidateAnalysisRuns(projectId);
      void queryClient.invalidateQueries({ queryKey: ['deviationJobs', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['surfaceDifferenceJobs', projectId] });

      const { topLayerId, bottomLayerId } = values.inputs;
      const runName = values.output.name || defaultCompareName(layerOf(topLayerId), layerOf(bottomLayerId));
      // Same predicate as the payload: no request, no tracker.
      const report = submission.input.reportOptions;
      const reportTitle = report?.title || runName;
      const trackReport = (batchId: string | null) => {
        if (report?.generate && batchId)
          store.set(reportTrackingAtom, (prev) => [
            ...prev,
            { toastId: undefined, batchId, projectId, title: reportTitle },
          ]);
      };

      // Cut & fill results are derived from the design (A), deviation results
      // from the as-built cloud (B): that is what each tracker watches for.
      const sourceOf: Record<CompareResult, string> = { cutFill: topLayerId, deviation: bottomLayerId };

      // Every start is a comparison group (ruling R21): a pair under the
      // server's group id, a single result as a one-member group under a
      // client-only one. So the toast speaks Compare surfaces from start to
      // finish, and each part settles on its OWN batch's primary layer.
      const parts: { part: CompareResult; batchId: string | null; failed?: string; uncertain?: boolean }[] =
        started.mutation === 'startComparison'
          ? (['cutFill', 'deviation'] as const).map((part) => {
              const batchId = started.batchIds[part];
              // No batch id and no message: the server did not start it
              // (`unknownReason` is for a job that ran and left no log).
              const failed = started.failed[part] ?? (batchId ? undefined : t('compareSurfaces.toast.notStarted'));
              const uncertain = failed === undefined && started.uncertain.includes(part);
              return { part, batchId, ...(failed !== undefined && { failed }), ...(uncertain && { uncertain }) };
            })
          : [
              {
                part: started.mutation === 'startSurfaceDifference' ? 'cutFill' : 'deviation',
                batchId: started.batchId,
              },
            ];
      const groupId = started.mutation === 'startComparison' ? started.groupId : `solo:${started.batchId}`;
      const reportBatchId = started.mutation === 'startComparison' ? started.reportBatchId : started.batchId;

      const members: ComparisonTrackerMember[] = parts.map(({ part, batchId, failed, uncertain }) => ({
        part,
        batchId: batchId ?? '',
        sourceLayerId: sourceOf[part],
        // The group's toast names the run.
        sourceLayerName: runName,
        existingDerivedLayerIds: derivedFrom(sourceOf[part]),
        ...(failed !== undefined && { failedToStart: { reason: failed } }),
        ...(uncertain && { startUncertain: true }),
      }));
      const partLabel = (m: ComparisonTrackerMember) => translateKey(t, COMPARE_PART_LABEL_KEYS[m.part]);
      const failureText = members
        .filter((m) => m.failedToStart)
        .map((m) =>
          translateKey(t, 'compareSurfaces.toast.partFailedReason', { part: partLabel(m), reason: m.failedToStart!.reason }),
        )
        .join(' · ');
      // R10: an uncertain start is no failure — say it may still be starting.
      const description = [
        failureText,
        ...members
          .filter((m) => m.startUncertain)
          .map((m) => translateKey(t, 'compareSurfaces.toast.mayStillBeStarting', { part: partLabel(m) })),
      ]
        .filter(Boolean)
        .join(' · ');

      // Nothing started: nothing to follow. Say why and keep the dialog open,
      // so the user can change what made the server refuse.
      if (members.every((m) => m.failedToStart)) {
        toast.error(translateKey(t, 'compareSurfaces.failedToStart', { message: failureText }));
        return;
      }

      const toastId = toast.loading(
        comparisonToastText(
          t,
          members.map(
            (m) =>
              ({
                part: m.part,
                partLabelKey: COMPARE_PART_LABEL_KEYS[m.part],
                sourceLayerName: m.sourceLayerName,
                ...(m.failedToStart && { partOutcome: { status: 'failed', reason: m.failedToStart.reason } }),
              }) as DeviationTracker,
          ),
        ),
        { duration: Infinity, ...(description && { description }) },
      );
      registerComparisonTrackers(store.set, {
        toastId,
        projectId,
        groupId,
        members,
        reportBatchId: reportBatchId ?? undefined,
        retryValues: values,
      });
      trackReport(reportBatchId);

      saveRemembered(projectId, values, edited);
      dismissActiveDialog();
    },
    // One error format for every start mutation (spec §7).
    onError: (err: Error) => toast.error(translateKey(t, 'compareSurfaces.failedToStart', { message: err.message })),
  });
}
